import { config, debug, i18n, inject, observer, React, Route, RouteComponentProps, ui } from "aaa";
import { AliasDeleteUsernameSigning } from "components/alias/AliasDeleteUsernameSigning";
import { AliasDeleteUsernameSuccess } from "components/alias/AliasDeleteUsernameSuccess";
import { AliasManageUsername } from "components/alias/AliasManageUsername";
import { AliasManageUsernameSigning } from "components/alias/AliasManageUsernameSigning";
import { AliasManageUsernameSuccess } from "components/alias/AliasManageUsernameSuccess";
import ErrorScreen from "components/common/ErrorScreen";
import auth from "stores/AuthStore";
import externalInterfaceStore, { ExternalInterfaceStore } from "stores/ExternalInterfaceStore";
import { pushRoute, replaceRoute } from "stores/RouterFunctions";
import { history } from "stores/RouterStore";
import { LoginManagementActivateGeorgeId } from "../LoginManagementActivateGeorgeId";
import { CardTanSettingsCards } from "./CardTanSettingsCards";
import { CardTanSettingsCardsSigning } from "./CardTanSettingsCardsSigning";
import { CardTanSettingsCardsSuccess } from "./CardTanSettingsCardsSuccess";
import { CardTanSettingsHomeScreen } from "./CardTanSettingsHomeScreen";
import { CardTanSettingsLimit } from "./CardTanSettingsLimit";
import { CardTanSettingsLimitSigning } from "./CardTanSettingsLimitSigning";
import { CardTanSettingsLimitSuccess } from "./CardTanSettingsLimitSuccess";
import { CardTanSettingsPin } from "./CardTanSettingsPin";
import { CardTanSettingsPinSigning } from "./CardTanSettingsPinSigning";
import { CardTanSettingsPinSuccess } from "./CardTanSettingsPinSuccess";
import { CardTanSettingsRoutes } from "./CardTanSettingsRoutes";
import { CardTanSettingsStore } from "./CardTanSettingsStore";
import { GidSettingsRoutes } from "../gidSettings/GidSettingsRoutes";
import { SIdentitySettingsRoutes } from "../sIdentitySettings/SIdentitySettingsRoutes";
import { CardTanSettingsActivateGeorgeIdSigning } from "./CardTanSettingsActivateGeorgeIdSigning";
import { CardTanSettingsActivateGeorgeIdSuccess } from "./CardTanSettingsActivateGeorgeIdSuccess";
import { CardTanSettingsNewDevice } from "./CardTanSettingsNewDevice";
import { NewAddress } from "components/common/NewAddress";
import { CardTanSettingsNewDeviceSigning } from "./CardTanSettingsNewDeviceSigning";
import { CardTanSettingsNewDeviceSuccess } from "./CardTanSettingsNewDeviceSuccess";

type IProps = RouteComponentProps & i18n.InjectedIntlProps & {
    externalInterfaceStore: ExternalInterfaceStore;
};

@i18n.injectIntl
@inject("externalInterfaceStore")
@observer
export default class CardTanSettingsContainer extends React.Component<IProps> {
    store: CardTanSettingsStore;

    constructor(props: IProps) {
        super(props);
        this.store = new CardTanSettingsStore();
    }

    async componentDidMount() {
        try {
            if (auth.authorized) {
                await this.store.loadSettings();
            }

            const authorizationType = externalInterfaceStore.settings?.authorizationType;

            if (history.location.pathname.startsWith(CardTanSettingsRoutes.LIMIT)) {
                if (history.location.pathname !== CardTanSettingsRoutes.LIMIT) {
                    replaceRoute(CardTanSettingsRoutes.LIMIT);
                }
            } else if (history.location.pathname.startsWith(CardTanSettingsRoutes.ONBOARDING_LIMIT)) {
                if (authorizationType === "CARDTAN") {
                    if (history.location.pathname !== CardTanSettingsRoutes.ONBOARDING_LIMIT) {
                        replaceRoute(CardTanSettingsRoutes.ONBOARDING_LIMIT);
                    }

                    await this.store.loadLimits();
                    return;
                } else {
                    if (authorizationType === "GEORGE_TOKEN") {
                        replaceRoute(GidSettingsRoutes.ONBOARDING_LIMIT);
                        return;
                    } else if (authorizationType === "SID") {
                        replaceRoute(SIdentitySettingsRoutes.ONBOARDING_LIMIT);
                        return;
                    }
                }
            } else if (history.location.pathname !== CardTanSettingsRoutes.ROOT) {
                pushRoute(CardTanSettingsRoutes.ROOT);
            }

            if (authorizationType !== "CARDTAN") {
                replaceRoute(CardTanSettingsRoutes.ERROR_SIGNING_METHOD);
                return;
            }

            if (auth.authorized) {
                // load offer asynchronious
                this.store.loadCardTanReaderOfferAndPrice();
                await this.store.loadData();
            }
        } catch (error) {
            debug.log("### CardTanSettingsContainer.componentDidMount() Failed to load data", error);

            if (!config.DEBUG) {
                pushRoute("/error");
            }
        }
    }

    withStore(Component: any) {
        return () => <Component {...this.props} store={this.store} />;
    }

    withContactsStore(Component: any) {
        return () => <Component {...this.props} contactsStore={this.store.contactsStore} />;
    }

    withAliasStore(Component: any) {
        return () => <Component {...this.props} store={this.store.aliasStore} />;
    }

    withMessage(Component: any, message: string, title?: string, closeRoute?: string, buttonLabelId?: i18n.IDS, onDismiss?: () => void) {
        return () => <Component
            {...this.props}
            useWebVersion
            variant="error"
            message={message}
            title={title}
            {...closeRoute
                ? {
                    buttonLabelId: buttonLabelId ?? "common.tryAgain",
                    onDismiss: () => {
                        onDismiss?.();
                        pushRoute(closeRoute);
                    }
                }
                : {}}
        />;
    }

    render() {
        return (
            <div>
                {/* currently the homescreen is not necessary - subroutes are directly used by the native app */}
                {/* <Route exact path={CardTanSettingsRoutes.ROOT} component={this.withMessage(ErrorScreen, ui.__("error.pageNotFound"), true)} /> */}
                <Route exact path={CardTanSettingsRoutes.ROOT} component={this.withStore(CardTanSettingsHomeScreen)} />
                <Route path={CardTanSettingsRoutes.USERMANAGEMENT_USERNAME} component={this.withAliasStore(AliasManageUsername)} />
                <Route path={CardTanSettingsRoutes.USERMANAGEMENT_USERNAME_SIGNING} component={this.withAliasStore(AliasManageUsernameSigning)} />
                <Route path={CardTanSettingsRoutes.USERMANAGEMENT_USERNAME_SUCCESS} component={this.withAliasStore(AliasManageUsernameSuccess)} />
                <Route path={CardTanSettingsRoutes.USERMANAGEMENT_DELETE_USERNAME_SIGNING} component={this.withAliasStore(AliasDeleteUsernameSigning)} />
                <Route path={CardTanSettingsRoutes.USERMANAGEMENT_DELETE_USERNAME_SUCCESS} component={this.withAliasStore(AliasDeleteUsernameSuccess)} />
                <Route exact path={CardTanSettingsRoutes.ONBOARDING_LIMIT} component={this.withStore(CardTanSettingsLimit)} />
                <Route path={CardTanSettingsRoutes.ONBOARDING_LIMIT_SIGNING} component={this.withStore(CardTanSettingsLimitSigning)} />
                <Route path={CardTanSettingsRoutes.ONBOARDING_LIMIT_SUCCESS} component={this.withStore(CardTanSettingsLimitSuccess)} />
                <Route exact path={CardTanSettingsRoutes.LIMIT} component={this.withStore(CardTanSettingsLimit)} />
                <Route path={CardTanSettingsRoutes.LIMIT_SUCCESS} component={this.withStore(CardTanSettingsLimitSuccess)} />
                <Route path={CardTanSettingsRoutes.LIMIT_SIGNING} component={this.withStore(CardTanSettingsLimitSigning)} />
                <Route path={CardTanSettingsRoutes.CARDS} component={this.withStore(CardTanSettingsCards)} />
                <Route path={CardTanSettingsRoutes.CARDS_SIGNING} component={this.withStore(CardTanSettingsCardsSigning)} />
                <Route path={CardTanSettingsRoutes.CARDS_SUCCESS} component={this.withStore(CardTanSettingsCardsSuccess)} />
                <Route path={CardTanSettingsRoutes.CHANGE_PIN} component={this.withStore(CardTanSettingsPin)} />
                <Route path={CardTanSettingsRoutes.CHANGE_PIN_SIGNING} component={this.withStore(CardTanSettingsPinSigning)} />
                <Route path={CardTanSettingsRoutes.CHANGE_PIN_SUCCESS} component={this.withStore(CardTanSettingsPinSuccess)} />
                <Route path={CardTanSettingsRoutes.NEW_DEVICE} component={this.withStore(CardTanSettingsNewDevice)} />
                <Route path={CardTanSettingsRoutes.NEW_DEVICE_SIGNING} component={this.withStore(CardTanSettingsNewDeviceSigning)} />
                <Route path={CardTanSettingsRoutes.NEW_DEVICE_SUCCESS} component={this.withStore(CardTanSettingsNewDeviceSuccess)} />
                <Route path={CardTanSettingsRoutes.ACTIVATE_GEORGE_ID} component={this.withStore(LoginManagementActivateGeorgeId)} />
                <Route path={CardTanSettingsRoutes.ACTIVATE_GEORGE_ID_SIGNING} component={this.withStore(CardTanSettingsActivateGeorgeIdSigning)} />
                <Route path={CardTanSettingsRoutes.ACTIVATE_GEORGE_ID_SUCCESS} component={this.withStore(CardTanSettingsActivateGeorgeIdSuccess)} />
                <Route path={CardTanSettingsRoutes.NEW_ADDRESS} component={this.withContactsStore(NewAddress)} />
                <Route path={CardTanSettingsRoutes.UPDATE_ADDRESS} component={this.withContactsStore(NewAddress)} />
                <Route
                    path={CardTanSettingsRoutes.ERROR_SIGNING_METHOD}
                    render={this.withMessage(
                        ErrorScreen,
                        ui.__("sidentitySettings.error.signingMethod")
                    )}
                />
                <Route
                    path={CardTanSettingsRoutes.CHANGE_PIN_ERROR}
                    render={this.withMessage(
                        ErrorScreen,
                        ui.__("cardTanSettings.pin.error.message"),
                        ui.__("cardTanSettings.pin.error.title"),
                        CardTanSettingsRoutes.CHANGE_PIN
                    )}
                />
                <Route
                    path={CardTanSettingsRoutes.CARDS_ERROR}
                    render={this.withMessage(
                        ErrorScreen,
                        ui.__("cardTanSettings.debitCards.error.message"),
                        ui.__("cardTanSettings.debitCards.error.title"),
                        CardTanSettingsRoutes.CARDS
                    )}
                />
                <Route
                    path={CardTanSettingsRoutes.NEW_DEVICE_ERROR_NO_OFFER}
                    render={this.withMessage(
                        ErrorScreen,
                        ui.__("cardTanSettings.newDevice.error.message"),
                        ui.__("cardTanSettings.newDevice.error.title"),
                        CardTanSettingsRoutes.ROOT,
                        "common.close",
                        () => {
                            this.store.isLoadingCardTanReaderOfferFinished = false;
                            this.store.loadCardTanReaderOfferAndPrice();
                        }
                    )}
                />
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/loginManagement/cardTanSettings/CardTanSettingsContainer.tsx